<template>
  <div
    v-if="organizations.length > 1"
    class="flex max-h-11 w-full items-center gap-4 pr-1 pl-7 text-primary-600 transition-colors duration-300 hover:bg-primary-200"
  >
    <AppIcon
      name="i-lucide-building"
      :width="24"
      alt=""
      aria-hidden="true"
      class="size-6 min-h-6 min-w-6 shrink-0"
    />
    <USelectMenu
      v-model="selected"
      v-model:open="isOpen"
      :search-input="false"
      data-testid="organization-select"
      :items="organizationItems"
      value-key="id"
      label-key="name"
      :placeholder="placeholder"
      :loading="loading"
      :ui="{
        base: selectBaseClass,
        value: 'text-[1rem]',
      }"
      @update:open="handleOpenChange"
    >
      <template #item="{ item }">
        <span class="truncate font-mainFont text-sm text-primary-600">{{
          item.name
        }}</span>
      </template>
      <template #trailing>
        <AppIcon
          v-show="hovered"
          name="i-heroicons-chevron-down-20-solid"
          class="transition-transform duration-300"
          :size="22"
        />
      </template>
    </USelectMenu>
  </div>
</template>

<script>
import {
  getUserGroups,
  getUserAttribute,
  setUserAttribute,
} from '~/utils/authv2'

export default {
  props: {
    hovered: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['dropdownOpen', 'dropdownClose', 'organizationChange'],
  data() {
    return {
      organizations: [],
      selected: null,
      isOpen: false,
      loading: false,
    }
  },
  computed: {
    organizationItems() {
      return this.organizations.map((org) => ({
        id: org.id,
        name: org.name.toUpperCase(),
      }))
    },
    placeholder() {
      return this.selected?.name ?? this.$t('user_panel.select_organization')
    },
    selectBaseClass() {
      return [
        'w-full relative block flex justify-start pl-0 font-mainFont cursor-pointer',
        this.hovered ? 'text-primary-600' : 'text-transparent',
      ].join(' ')
    },
  },
  watch: {
    selected(newVal, oldVal) {
      if (newVal && oldVal && newVal !== oldVal) {
        const userId = this.$keycloak?.tokenParsed?.sub
        if (userId) {
          setUserAttribute(userId, 'statistics_group_id', newVal)
        }
        const org = this.organizations.find((o) => o.id === newVal)
        if (org) this.$emit('organizationChange', org)
      }
    },
  },
  async created() {
    this.loading = true
    try {
      const { organizations } = await getUserGroups({
        usersOnly: true,
        includeCounts: false,
      })
      this.organizations = organizations
      if (organizations.length === 0) return

      const userId = this.$keycloak?.tokenParsed?.sub
      let savedGroupId = null
      if (userId) {
        try {
          const response = await getUserAttribute(userId)
          if (response?.attributes?.statistics_group_id) {
            savedGroupId = response.attributes.statistics_group_id[0]
            if (!organizations.some((org) => org.id === savedGroupId)) {
              savedGroupId = organizations[0].id
              await setUserAttribute(
                userId,
                'statistics_group_id',
                savedGroupId
              )
            }
          } else {
            savedGroupId = organizations[0].id
            await setUserAttribute(userId, 'statistics_group_id', savedGroupId)
          }
        } catch {
          // attribute not set yet, fall through to default
        }
      }

      const match = savedGroupId
        ? organizations.find((org) => org.id === savedGroupId)
        : null
      const initial = match ?? organizations[0]
      this.selected = initial.id
    } finally {
      this.loading = false
    }
  },
  methods: {
    handleOpenChange(open) {
      this.isOpen = open
      if (open) {
        this.$emit('dropdownOpen')
      } else {
        this.$emit('dropdownClose')
      }
    },
  },
}
</script>
